import { FC, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useQuery } from 'react-query';

import API from 'core/Api';

import { useLanguageContext } from 'store/language';
import { useActiveClientContext } from 'store/active-client';
import { usePermissionsContext } from 'store/permissions';
import { PermissionKeys } from 'store/types';

import { buildClientEntity } from 'utils/helpers/client';

import InnerWrapper from 'routes/client/reports/InnerWrapper';
import { getUserInfo } from 'utils/auth';
import { getSubdomain } from 'utils/helpers/routing';
import NotAuthorizedScreen from 'routes/not-authorized';
import ReportForm from './components/ReportForm';

const EmployeeData: FC = () => {
  const methods = useForm();
  const openLinkBtn = useRef<HTMLAnchorElement>(null);
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { checkPermission } = usePermissionsContext();
  const isOperator = getSubdomain() === 'operators';
  const userInfo = getUserInfo();

  const [link, setLink] = useState<string | null>(null);

  const {
    isLoading: isLoadingDropdowns,
    error: errorDropdowns,
    data: dropdowns,
  } = useQuery<any, Error>([buildClientEntity('report-employee-employee-data-dropdowns', client)], (): Promise<any> => {
    return API.get('reports/employee/employee-data/dropdown-options').then(res => {
      return res.data[language || 'bg'];
    });
  });

  const handleSubmitReport = (data: any) => {
    const formattedData = {
      user_id: data.user_id?.map((user: any) => user.value),
      group_id: data.group_id?.map((group: any) => group.value),
      status: data.status?.map((status: any) => status.value),
    };

    return API.post('reports/employee/employee-data', formattedData).then(res => {
      setLink(res.data.url);
    });
  };

  useEffect(() => {
    if (link) openLinkBtn?.current?.click();
  }, [link]);

  const hasAccess = isOperator || checkPermission(PermissionKeys.CAN_DOWNLOAD_USER_DATA_REPORT);

  if (hasAccess) {
    return (
      <InnerWrapper
        isLoading={isLoadingDropdowns}
        error={errorDropdowns}
      >
        {dropdowns && (
          <ReportForm
            data={dropdowns}
            methods={methods}
            onSubmit={handleSubmitReport}
          />
        )}
        {link && (
          <Link
            ref={openLinkBtn}
            to={{
              pathname: link,
            }}
            target="_blank"
            style={{ display: 'none' }}
          />
        )}
      </InnerWrapper>
    );
  }

  return <NotAuthorizedScreen />;
};

export default EmployeeData;
